import { DocPageStatus, DocPageSummaryFragment } from '../../generated'
import { DocTreeNode } from '../interfaceTypes/docs'

/**
 * Builds a nested tree from a flat list of pages. A page whose parent is not in
 * the visible set is promoted to a root so it never becomes unreachable.
 */
export const buildDocTree = (pages: DocPageSummaryFragment[]): DocTreeNode[] => {
  const byId = new Map<string, DocTreeNode>()
  pages.forEach((page) => byId.set(page.id, { ...page, children: [], depth: 0 }))

  const roots: DocTreeNode[] = []
  byId.forEach((node) => {
    const parent = node.parentId ? byId.get(node.parentId) : undefined
    if (parent) parent.children.push(node)
    else roots.push(node)
  })

  const assignDepth = (nodes: DocTreeNode[], depth: number) => {
    nodes
      .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title))
      .forEach((node) => {
        node.depth = depth
        assignDepth(node.children, depth + 1)
      })
  }
  assignDepth(roots, 0)
  return roots
}

/** Flattens a tree into a depth-first ordered list (used for prev/next, search). */
export const flattenDocTree = (nodes: DocTreeNode[]): DocTreeNode[] =>
  nodes.reduce<DocTreeNode[]>((acc, node) => [...acc, node, ...flattenDocTree(node.children)], [])

export const isPublished = (status: DocPageStatus) => status === DocPageStatus.Published

/**
 * Filters a tree by title. A node is kept when it (or any descendant) matches,
 * so the path to a match stays visible. Returns matched ids so the caller can
 * auto-expand them.
 */
export const filterDocTree = (
  nodes: DocTreeNode[],
  query: string,
): { tree: DocTreeNode[]; matchedIds: Set<string> } => {
  const q = query.trim().toLowerCase()
  const matchedIds = new Set<string>()
  if (!q) return { tree: nodes, matchedIds }

  const walk = (list: DocTreeNode[]): DocTreeNode[] =>
    list.reduce<DocTreeNode[]>((acc, node) => {
      const children = walk(node.children)
      const selfMatch = (node.title || '').toLowerCase().includes(q)
      if (selfMatch || children.length) {
        if (selfMatch || children.length) matchedIds.add(node.id)
        acc.push({ ...node, children })
      }
      return acc
    }, [])

  return { tree: walk(nodes), matchedIds }
}

export type DocReorderPosition = 'before' | 'after' | 'inside'

export interface DocReorderItem {
  id: string
  parentId?: string | null
  sortOrder: number
}

/** True when `nodeId` is `ancestorId` itself or sits anywhere beneath it. */
const isSelfOrDescendant = (
  pages: DocPageSummaryFragment[],
  ancestorId: string,
  nodeId: string | null,
): boolean => {
  let current = nodeId
  while (current) {
    if (current === ancestorId) return true
    const cursor = current
    current = pages.find((p) => p.id === cursor)?.parentId || null
  }
  return false
}

/**
 * Computes the minimal reorder payload for dropping `dragId` relative to
 * `targetId`. `inside` nests under the target; `before`/`after` place it as a
 * sibling of the target (which also handles re-parenting and root promotion).
 * Returns `[]` for no-op or illegal moves (e.g. into the node's own subtree).
 */
export const computeDocReorder = (
  pages: DocPageSummaryFragment[],
  dragId: string,
  targetId: string,
  position: DocReorderPosition,
): DocReorderItem[] => {
  if (dragId === targetId) return []
  const target = pages.find((p) => p.id === targetId)
  const drag = pages.find((p) => p.id === dragId)
  if (!target || !drag) return []

  const newParentId = position === 'inside' ? targetId : target.parentId || null
  // Cannot drop a node inside itself or any of its own descendants.
  if (isSelfOrDescendant(pages, dragId, newParentId)) return []

  const siblings = pages
    .filter((p) => (p.parentId || null) === newParentId && p.id !== dragId)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title))

  let insertIndex: number
  if (position === 'inside') {
    insertIndex = siblings.length
  } else {
    const ti = siblings.findIndex((p) => p.id === targetId)
    insertIndex = position === 'before' ? ti : ti + 1
  }
  if (insertIndex < 0) insertIndex = siblings.length

  const ordered = [...siblings.slice(0, insertIndex), drag, ...siblings.slice(insertIndex)]
  return ordered.map((p, index) => ({ id: p.id, parentId: newParentId, sortOrder: index }))
}
